import { Fragment, useEffect, useState, useRef, useMemo } from "react";
import OrganizationsSettingsTable from "../../components/NotificationsScreen/OrganizationsSettingsTable";
import HeaderColumnData from "../../services/models/notificationsSettingsTableModels/headerColumnData.model";
import BodyColumnData from "../../components/NotificationsScreen/BodyColumnData.model";
import UpdateOrganizationSettingsPayloadItem from "../../services/models/notificationsModels/updateOrganizationSettingsPayloadItem";
import { useHistory, useRouteMatch } from "react-router-dom";
import RouteLeavingGuard from "./RouteLeaving";
import SubHeader from "../../components/global/SubHeader";
import axiosInstance from "../../services/api";
import toastMessages from "../../services/utils/helpers/toastMessage";
import { useOktaAuth } from "@okta/okta-react";
import useOrganizationNotificationMediumValues from "../../services/utils/custom hooks/Notifications/useOrganizationMediumValues";
import _ from "lodash";
import useOrganizations from "../../services/utils/custom hooks/useOrganizations";
import useMediumValues from "../../services/utils/custom hooks/Notifications/useMediumValues";
import CustomPopUp from "../../components/global/CustomPopup";
import CustomText from "../../components/atomic/CustomText";
import Button from "../../components/organism/Button";
import useProfile from "../../services/utils/custom hooks/useProfile";

function OrganisationSettings() {
  const [tableColumns, setTableColumns] = useState<HeaderColumnData[]>([]);
  const { authState } = useOktaAuth();
  const [tableBody, setTableBody] = useState<Array<BodyColumnData[]>>([]);
  // const [
  //   initialOrganizationNotificationMediumTableBody,
  //   setinitialOrganizationNotificationMediumTableBody,
  // ] = useState<Array<BodyColumnData[]>>([]);
  const profile = useProfile();
  const isPhysicianOnly =
    profile?.data?.user?.roles?.includes("Physician") &&
    profile?.data?.user?.roles?.length === 1;
  const history = useHistory()
  const [initialSet, setInitialSet] = useState(false);
  const [saving, setSaving] = useState<boolean>(false);

  const { refetch: refetchingOrgNotificationMediumValues } =
    useOrganizationNotificationMediumValues();
  const [showPopup, setShowpopup] = useState<boolean>(false);
  const { refetch: refetchingOrganizations } = useOrganizations();
  const { refetch: refetchingMediumValues } = useMediumValues();

  const [
    initialOrganizationNotificationMediumTableBody,
    setinitialOrganizationNotificationMediumTableBody,
  ] = useState<any>([]);

  useEffect(() => {
    if (tableBody.length > 0 && !initialSet) {
      setinitialOrganizationNotificationMediumTableBody(_.cloneDeep(tableBody));
      setInitialSet(true);
    }
  }, [tableBody, initialSet]);

  useEffect(() => {

  }, [initialOrganizationNotificationMediumTableBody]);

  const isOrganizationNotificationSettingsChanged = useMemo(() => {
    if (!initialSet) return false;
    return (
      JSON.stringify(tableBody) !==
      JSON.stringify(initialOrganizationNotificationMediumTableBody)
    );
  }, [tableBody, initialSet, initialOrganizationNotificationMediumTableBody]);

  async function handleSave() {
    let apiData: any = [];
    tableBody.forEach((data) => {
      data.forEach((value) => {
        if (Array.isArray(value.data)) {
          let organizationId = !Array.isArray(data[0].data)
            ? Number(data[0].data.id)
            : 0;

          value.data.forEach((setting) => {
            let settingItem = {
              organizationId,
              notificationMediumId: Number(setting.id),
              value: typeof setting.value === "boolean" ? setting.value : false,
            };

            apiData.push(settingItem);
          });
        }
      });
    });

    const body = { data: apiData };

    setSaving(true);
    try {
      const response = await axiosInstance.post(
        "v1/organization-notification-settings",
        body,
        {
          headers: {
            Authorization: `Bearer ${authState?.accessToken?.accessToken}`,
          },
        }
      );
      if (response.status === 200 && !!response.data.message) {
        await Promise.all([
          refetchingMediumValues(),
          refetchingOrganizations(),
          refetchingOrgNotificationMediumValues(),
        ]);

        setinitialOrganizationNotificationMediumTableBody(
          _.cloneDeep(tableBody)
        );
        setSaving(false);
        setShowpopup(false)
        toastMessages.success("Settings saved successfully.");
      } else {
        setSaving(false);
        setShowpopup(false)
      }
    } catch (error: any) {
      toastMessages.error(
        error.message || "An error occurred while saving settings."
      );
      setSaving(false);
      setShowpopup(false)
    }
  }

  useEffect(() => {
    if (isPhysicianOnly) {
      history.push("/unauthorized");
    }
  }, [isPhysicianOnly])

  return (
    <Fragment>
      <RouteLeavingGuard
        when={isOrganizationNotificationSettingsChanged}
        navigate={(path) => {
          history.push(path);
        }}
        shouldBlockNavigation={() => isOrganizationNotificationSettingsChanged}
      />
      <SubHeader
        title="Organization preferences"
        hideSubHeaderSelectors
        showButtonInSubheaderForMobile
        containerClass="md:flex"
        customButtons={[
          {
            textInformation: {
              text: "save",
              fontWeight: "font-semibold",
            },
            onClick: () => setShowpopup(true),
            buttonTheme: "primary",
            // disabled: saving,
            // loading: saving,
            className: "my-2 mx-5",
          },
        ]}
      />
      <Fragment>
        {showPopup && (
          <CustomPopUp
            hidePopUp={() => setShowpopup(false)}
            popUpTitle={"Major Impact on Users"}
            onlineOrder={false}
            hideClose={false}
            warningPopupNotificationSettings
            subTitleClassName="text-[#FE4F4F]"
          >
            <div>
              <CustomText
                text="Changing the organization's notification settings or report delivery method will affect all users within the organization."
                fontWeight="font-normal"
                className="text-requiredRed mb-5"
              />
              <CustomText
                text="They will receive notifications accordingly. Please proceed with caution."
                fontWeight="font-normal"
              />
              <div className="flex justify-end mt-5">
                <Button
                  buttonTheme="primary"
                  onClick={() => handleSave()}
                  textInformation={{
                    text: "save",
                    fontWeight: "font-semibold",
                  }}
                  loading={saving}
                  disabled={saving}
                />
              </div>
            </div>
          </CustomPopUp>
        )}
        <div className="px-6 overflow-scroll">
          <p className="text-royalBlue font-bold text-xl pb-6 py-8">
            Notification Medium / Report Delivery
          </p>

          <OrganizationsSettingsTable
            tableBody={tableBody}
            tableColumns={tableColumns}
            setTableBody={setTableBody}
            setTableColumns={setTableColumns}
          />
        </div>
      </Fragment>
    </Fragment>
  );
}

export default OrganisationSettings;
